/* ===================================================================
   Evaluation helpers — score processed entries against an EvalSet's
   answer key, replay an eval's memo + phase timelines into MemosMap,
   and compare the live memo against an expected final state.
=================================================================== */
import { cloneJSON } from "./utils";
import type {
  EvalResult,
  EvalRow,
  MemoMatchResult,
  MemosMap,
  ProcessedEntry,
  Approval,
  MemoState,
} from "./types";

/* ----- buildMemosFromEval ----------
 * Eval sets may supply a memoTimeline (per-email writes) and a phaseTimeline
 * (per-email phase progressions). We replay those into a memo map so the
 * History panel can compare runs without having to actually re-run them. */
interface MemoTimelineEntry {
  ws: string;
  key: string;
  value: string;
  source?: string;
}
interface PhaseTimelineEntry { ws: string; name: string }
interface EvalLike {
  memoTimeline?: Record<string | number, MemoTimelineEntry[]>;
  phaseTimeline?: Record<string | number, PhaseTimelineEntry>;
  finalState?: { deal_memo?: { fields: Array<{ key: string; final_value: string }> } };
  memoMatterWs?: string;
}
interface ProcessedLikeEmail { id: number }

export function buildMemosFromEval(
  selEval: EvalLike,
  baseMemos: MemosMap,
  processedEmails: ProcessedLikeEmail[],
): MemosMap {
  const memos = cloneJSON(baseMemos);
  const ids = processedEmails.map((e) => e.id);
  const idSet = new Set(ids);
  function write(ws: string, k: string, v: string, source?: string) {
    const m = memos[ws] || (memos[ws] = { phase: "Setup", fields: {} });
    const prev = m.fields[k];
    const hist = prev ? [...(prev.history || []), { value: prev.value, source: prev.source }] : [];
    m.fields[k] = { value: v, source, history: hist };
  }
  ids.forEach((id) => {
    ((selEval.memoTimeline && selEval.memoTimeline[id]) || []).forEach((w) =>
      write(w.ws, w.key, w.value, w.source),
    );
  });
  if (selEval.phaseTimeline) {
    Object.keys(selEval.phaseTimeline).forEach((k) => {
      const id = Number(k);
      if (!idSet.has(id)) return;
      const ph = selEval.phaseTimeline![k];
      const m = (memos[ph.ws] || (memos[ph.ws] = { phase: "Setup", fields: {} })) as MemoState & {
        _phaseAt?: number;
      };
      m._phaseAt = Math.max(m._phaseAt || 0, id);
      if ((m._phaseAt || 0) === id) m.phase = ph.name;
    });
  }
  Object.values(memos).forEach((m) => {
    if (m) delete (m as MemoState & { _phaseAt?: number })._phaseAt;
  });
  return memos;
}

/* ----- computeMemoMatch ----------
 * Score the populated deal memo against the eval set's expected final state. */
export function computeMemoMatch(
  memos: MemosMap,
  selEval: EvalLike | null | undefined,
): MemoMatchResult | null {
  if (!selEval || !selEval.finalState || !selEval.finalState.deal_memo) return null;
  const ws =
    selEval.memoMatterWs ||
    Object.keys(memos).find((k) => Object.keys(memos[k]?.fields || {}).length > 0) ||
    Object.keys(memos)[0] ||
    "";
  const fields = selEval.finalState.deal_memo.fields || [];
  const live = ((memos[ws] || { fields: {} }).fields) || {};
  const rows = fields.map((f) => {
    const got = live[f.key] ? live[f.key].value : null;
    return { key: f.key, expected: f.final_value, got, ok: got === f.final_value };
  });
  const n = rows.length;
  const ok = rows.filter((r) => r.ok).length;
  return { rows, n, ok, pct: n ? Math.round(ok / n * 100) : 0 };
}

/* ----- computeEval ----------
 * Each email is scored on the SET of dimensions its answer key expects, not
 * the first match. Every expected dimension (conflict, privilege, newpart,
 * phase, anomaly, multi, noise) is evaluated independently and contributes
 * to detection recall. An email expected to fire both conflict AND
 * privilege is scored on both. */
const DIM_LABEL: Record<string, string> = {
  conflict: "conflict",
  multi: "multi-match",
  privilege: "privilege",
  newpart: "new participant",
  phase: "phase",
  anomaly: "anomaly",
  noise: "noise→unassoc",
};

interface ExpectedEntry {
  catalog?: string[];
  conflict?: boolean;
  multi?: boolean;
  privilege?: boolean;
  newpart?: boolean;
  phase?: boolean;
  anomaly?: boolean;
  noise?: boolean;
  [k: string]: any;
}

export function computeEval(
  processed: ProcessedEntry[],
  approvals: Approval[],
  thresholds: Record<string, number>,
  toggledOff: Set<string>,
  expected: Record<number | string, ExpectedEntry> | null | undefined,
): EvalResult {
  if (!expected)
    return { rows: [], catN: 0, catOK: 0, catPct: 0, detN: 0, detOK: 0, detPct: 0, noEval: true };
  const rows: EvalRow[] = processed.map((p) => {
    const e = p.email;
    const ex = expected[e.id] || {};
    const top = (p.R.catalogs[0] && p.R.catalogs[0][0]) as string;
    const catalogOK = ex.catalog ? ex.catalog.includes(top) : true;
    const fired = approvals.filter((a) => a.emailId === e.id).map((a) => a.type);
    const detected: Record<string, boolean> = {
      conflict: fired.includes("conflict"),
      multi: p.R.catalogs.length > 1,
      privilege:
        !toggledOff.has("privilege") &&
        p.R.checks.some(
          (c) => c.id === "privilege" && c.conf >= (thresholds.privilege ?? 0.7),
        ),
      newpart: fired.includes("participant") || fired.includes("new_participant"),
      phase: fired.includes("phase"),
      anomaly: fired.includes("anomaly"),
      noise: top === "unassoc",
    };
    const dims = Object.keys(DIM_LABEL)
      .filter((k) => ex[k])
      .map((k) => ({ dim: DIM_LABEL[k], detectOK: !!detected[k] }));
    return { id: e.id, subject: e.subject, catalogOK, dims };
  });
  const catN = rows.length;
  const catOK = rows.filter((r) => r.catalogOK).length;
  let detN = 0;
  let detOK = 0;
  rows.forEach((r) =>
    r.dims.forEach((d) => {
      detN++;
      if (d.detectOK) detOK++;
    }),
  );
  return {
    rows,
    catN,
    catOK,
    catPct: catN ? Math.round(catOK / catN * 100) : 0,
    detN,
    detOK,
    detPct: detN ? Math.round(detOK / detN * 100) : 0,
  };
}
